import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import KeyResultProgress from '../components/KeyResultProgress.tsx';
import MonthlyIndicatorForm from '../components/MonthlyIndicatorForm.tsx';
import MonthlyIndicatorHistory from '../components/MonthlyIndicatorHistory.tsx';
import { addIndicator, createKeyResult, getObjective, listIndicators, updateKeyResult } from '../services/okrApi.ts';
import { formatDate, normalizeDate } from '../utils/dateUtils.ts';
import './Objectives.css';

interface KeyResult {
  id: number;
  objective_id: number;
  title: string;
  target_value: number;
  unit: string;
  status: 'active' | 'archived';
  current_value: number;
  progress_percent: number;
}

interface Objective {
  id: number;
  title: string;
  description: string;
  period_type: 'annual' | 'quarterly';
  period_start: string;
  period_end: string;
  status: 'active' | 'archived';
  key_results: KeyResult[];
}

interface MonthlyIndicator {
  id: number;
  key_result_id: number;
  month: string;
  value: number;
}

const ObjectiveDetail: React.FC = () => {
  const { id } = useParams();
  const objectiveId = Number(id);
  const [objective, setObjective] = useState<Objective | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingKeyResult, setEditingKeyResult] = useState<KeyResult | null>(null);
  const [keyResultEdit, setKeyResultEdit] = useState({
    title: '',
    target_value: 0,
    unit: '',
  });
  const [indicatorModalKeyResult, setIndicatorModalKeyResult] = useState<KeyResult | null>(null);
  const [historyModalKeyResult, setHistoryModalKeyResult] = useState<KeyResult | null>(null);
  const [indicators, setIndicators] = useState<MonthlyIndicator[]>([]);
  const [indicatorLoading, setIndicatorLoading] = useState(false);
  const [showNewKeyResultModal, setShowNewKeyResultModal] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState({
    title: '',
    target_value: 0,
    unit: '',
  });
  const [saving, setSaving] = useState(false);
  const [archivingKrId, setArchivingKrId] = useState<number | null>(null);

  const fetchObjective = async () => {
    if (!Number.isFinite(objectiveId)) return;
    try {
      const data = await getObjective(objectiveId);
      setObjective(data);
    } catch (error) {
      console.error('Erro ao carregar objetivo:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadIndicators = async (keyResultId: number) => {
    setIndicatorLoading(true);
    try {
      const data = await listIndicators(keyResultId);
      setIndicators(data);
    } catch (error) {
      console.error('Erro ao carregar indicadores:', error);
    } finally {
      setIndicatorLoading(false);
    }
  };

  useEffect(() => {
    fetchObjective();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectiveId]);

  const startEditKeyResult = (keyResult: KeyResult) => {
    setEditingKeyResult(keyResult);
    setKeyResultEdit({
      title: keyResult.title,
      target_value: keyResult.target_value,
      unit: keyResult.unit,
    });
  };

  const saveKeyResult = async () => {
    if (!editingKeyResult) return;
    setSaving(true);
    try {
      await updateKeyResult(editingKeyResult.id, {
        title: keyResultEdit.title,
        target_value: keyResultEdit.target_value,
        unit: keyResultEdit.unit,
      });
      setEditingKeyResult(null);
      fetchObjective();
    } catch (error) {
      console.error('Erro ao atualizar resultado:', error);
    } finally {
      setSaving(false);
    }
  };

  const openIndicatorModal = (keyResult: KeyResult) => {
    setIndicatorModalKeyResult(keyResult);
  };

  const openHistoryModal = async (keyResult: KeyResult) => {
    setHistoryModalKeyResult(keyResult);
    await loadIndicators(keyResult.id);
  };

  const closeModals = () => {
    setIndicatorModalKeyResult(null);
    setHistoryModalKeyResult(null);
  };

  const openNewKeyResultModal = () => {
    setNewKeyResult({ title: '', target_value: 0, unit: '' });
    setShowNewKeyResultModal(true);
  };

  const handleCreateKeyResult = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!objective) return;
    setSaving(true);
    try {
      await createKeyResult(objective.id, {
        title: newKeyResult.title,
        target_value: newKeyResult.target_value,
        unit: newKeyResult.unit,
      });
      setShowNewKeyResultModal(false);
      fetchObjective();
    } catch (error) {
      console.error('Erro ao adicionar resultado:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddIndicator = async (payload: { month: string; value: number }) => {
    if (!indicatorModalKeyResult) return;
    try {
      await addIndicator(indicatorModalKeyResult.id, payload);
      await fetchObjective();
      if (historyModalKeyResult?.id === indicatorModalKeyResult.id) {
        await loadIndicators(indicatorModalKeyResult.id);
      }
      setIndicatorModalKeyResult(null);
    } catch (error) {
      console.error('Erro ao registrar indicador:', error);
    }
  };

  const archiveKeyResult = async (keyResult: KeyResult) => {
    setArchivingKrId(keyResult.id);
    try {
      await updateKeyResult(keyResult.id, { status: 'archived' });
      fetchObjective();
    } catch (error) {
      console.error('Erro ao arquivar resultado:', error);
    } finally {
      setArchivingKrId(null);
    }
  };

  if (loading) {
    return (
      <div className="objective-detail">
        <h1>Objetivo</h1>
        <p>Carregando...</p>
      </div>
    );
  }

  if (!objective) {
    return (
      <div className="objective-detail">
        <h1>Objetivo</h1>
        <p>Não encontrado.</p>
      </div>
    );
  }

  return (
    <div className="objective-detail">
      <div className="page-header">
        <div>
          <h1>{objective.title}</h1>
          <p>{objective.description}</p>
        </div>
        <div className="objective-actions">
          <Link className="secondary-button" to="/objectives">
            Voltar
          </Link>
        </div>
      </div>

      <div className="objective-meta">
        <span>Período: {objective.period_type === 'annual' ? 'Anual' : 'Trimestral'}</span>
        <span>
          {formatDate(normalizeDate(objective.period_start))} - {formatDate(normalizeDate(objective.period_end))}
        </span>
        <span>Status: {objective.status === 'active' ? 'Ativo' : 'Arquivado'}</span>
      </div>

      <div className="objective-detail-content">
        <div className="section-header">
          <h3>Key Results</h3>
        </div>
        <div className="section-actions">
          <button className="secondary-button" onClick={openNewKeyResultModal}>
            <img src="/add.svg" alt="" className="button-icon-secondary" />
            Novo resultado
          </button>
        </div>
        <div className="key-results-list">
          {objective.key_results?.length ? (
            objective.key_results.map((kr) => (
              <div key={kr.id} className="key-result-item">
                <div className="key-result-header">
                  <strong>{kr.title}</strong>
                  <div className="key-result-actions">
                    <button
                      className="icon-button edit-button"
                      onClick={() => startEditKeyResult(kr)}
                      title="Editar resultado"
                    >
                      <img src="/edit.svg" alt="Editar" className="icon" />
                    </button>
                    <button
                      className="icon-button add-button"
                      onClick={() => openIndicatorModal(kr)}
                      title="Registrar indicador"
                    >
                      <img src="/add.svg" alt="Registrar indicador" className="icon" />
                    </button>
                    <button
                      className="icon-button view-button"
                      onClick={() => openHistoryModal(kr)}
                      title="Ver histórico"
                    >
                      <img src="/eye.svg" alt="Ver histórico" className="icon" />
                    </button>
                    {objective.key_results.length > 2 && kr.status !== 'archived' && (
                      <button
                        className="icon-button archive-button"
                        onClick={() => archiveKeyResult(kr)}
                        title="Arquivar resultado"
                        disabled={archivingKrId === kr.id}
                      >
                        {archivingKrId === kr.id ? '...' : <img src="/delete.svg" alt="Arquivar" className="icon" />}
                      </button>
                    )}
                  </div>
                </div>
                <KeyResultProgress
                  currentValue={kr.current_value}
                  targetValue={kr.target_value}
                  unit={kr.unit}
                  progressPercent={kr.progress_percent}
                />
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>Nenhum resultado cadastrado.</p>
            </div>
          )}
        </div>
      </div>

      {editingKeyResult && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>Editar resultado</h3>
              <button className="icon-button" onClick={() => setEditingKeyResult(null)} title="Fechar">
                <img src="/x.svg" alt="Fechar" className="icon" />
              </button>
            </div>
            <form
              className="objective-form"
              onSubmit={(event) => {
                event.preventDefault();
                saveKeyResult();
              }}
            >
              <div className="form-group">
                <label htmlFor="kr-edit-title">Resultado *</label>
                <input
                  type="text"
                  id="kr-edit-title"
                  value={keyResultEdit.title}
                  onChange={(e) => setKeyResultEdit({ ...keyResultEdit, title: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="kr-edit-target">Meta *</label>
                  <input
                    type="number"
                    id="kr-edit-target"
                    value={keyResultEdit.target_value}
                    onChange={(e) => setKeyResultEdit({ ...keyResultEdit, target_value: Number(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="kr-edit-unit">Unidade *</label>
                  <input
                    type="text"
                    id="kr-edit-unit"
                    value={keyResultEdit.unit}
                    onChange={(e) => setKeyResultEdit({ ...keyResultEdit, unit: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar resultado'}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setEditingKeyResult(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {indicatorModalKeyResult && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>Registrar indicador</h3>
              <button className="icon-button" onClick={closeModals} title="Fechar">
                <img src="/x.svg" alt="Fechar" className="icon" />
              </button>
            </div>
            <p className="modal-subtitle">{indicatorModalKeyResult.title}</p>
            <MonthlyIndicatorForm onSubmit={handleAddIndicator} />
          </div>
        </div>
      )}

      {historyModalKeyResult && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>Histórico mensal</h3>
              <button className="icon-button" onClick={closeModals} title="Fechar">
                <img src="/x.svg" alt="Fechar" className="icon" />
              </button>
            </div>
            <p className="modal-subtitle">{historyModalKeyResult.title}</p>
            {indicatorLoading ? (
              <p>Carregando indicadores...</p>
            ) : (
              <MonthlyIndicatorHistory indicators={indicators} unit={historyModalKeyResult.unit} />
            )}
          </div>
        </div>
      )}

      {showNewKeyResultModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>Novo resultado</h3>
              <button className="icon-button" onClick={() => setShowNewKeyResultModal(false)} title="Fechar">
                <img src="/x.svg" alt="Fechar" className="icon" />
              </button>
            </div>
            <form className="objective-form" onSubmit={handleCreateKeyResult}>
              <div className="form-group">
                <label htmlFor="kr-new-title">Resultado *</label>
                <input
                  type="text"
                  id="kr-new-title"
                  value={newKeyResult.title}
                  onChange={(e) => setNewKeyResult({ ...newKeyResult, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="kr-new-target">Meta *</label>
                  <input
                    type="number"
                    id="kr-new-target"
                    value={newKeyResult.target_value}
                    onChange={(e) => setNewKeyResult({ ...newKeyResult, target_value: Number(e.target.value) })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="kr-new-unit">Unidade *</label>
                  <input
                    type="text"
                    id="kr-new-unit"
                    value={newKeyResult.unit}
                    onChange={(e) => setNewKeyResult({ ...newKeyResult, unit: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'Adicionando...' : 'Adicionar resultado'}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowNewKeyResultModal(false)}
                  disabled={saving}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObjectiveDetail;
